import { Subscription , of } from 'rxjs';
import { AuthServer } from '@services/auth-server.service';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormGroup, FormBuilder, FormControl, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConfigService } from '@services/webFeeder/config.service';
import { NotificationsService } from 'angular2-notifications';
import { EmailDeposit } from '@model/email-deposit-model';
import { AppUtils } from '@utils/app.utils';
import { ConfirmationModalComponent } from '@component/confirmation-modal/confirmation-modal.component';
import {BANKS, Constants} from '@utils/constants';
import {DepositService} from '@services/webFeeder/deposit.service';

interface ISelectRedemption {
  bank: string;
  bankAccount: string;
  bankAgency: string;
  code: string;
}

@Component({
  selector: 'app-deposit',
  templateUrl: './deposit.component.html'
})
export class DepositComponent implements OnInit, OnDestroy {
  account: any = null;
  accountDesc: any = null;
  title = Constants.TITLE_CARD_MODAL_DEPOSIT;
  loading = false;
  tabIndex = 0;
  redemptionForm: FormGroup;
  depositForm: FormGroup;
  priceValuesFormatOptions = Constants.PRICE_VALUES_FORMAT_OPTIONS;
  isAssessor = this._configService.isUserAssessorLogged();
  selectedUser = this._configService.getInfoUserLogged();
  whitelabel = this._configService.getConfigWhitelabel();
  totalWithdrawalSubscription: Subscription;
  showRedemptionAccount: boolean = this.whitelabel.system.selectRedemptionAccount;
  listAccounts: ISelectRedemption[] = [];

  constructor(
    public dialogRef: MatDialogRef<DepositComponent>,
    private _configService: ConfigService,
    private _notificationService: NotificationsService,
    private _formBuilder: FormBuilder,
    private _authServer: AuthServer,
    private dialog: MatDialog,
    private depositService: DepositService
  ) {
    const bx = this._configService.getBrokerAccountSelected();
    if (this.selectedUser && bx && Number(this.selectedUser.account) === Number(bx)) {
      const name = this._configService.isLoginTypeWSO2() && this.selectedUser.full_name ? this.selectedUser.full_name : this.selectedUser.accountDesc;
      this.account = this.selectedUser.account + (name ? ' - ' + name : '');
    }
    this.buildForm();
  }

  ngOnInit() {
    if (this.whitelabel.deposit.announcementModal.active) {
      this.showAnnouncementModal();
    }
    if (this.showRedemptionAccount) {
      this.getBankAccounts();
    }
  }

  ngOnDestroy() {
    if (this.totalWithdrawalSubscription) {
      this.totalWithdrawalSubscription.unsubscribe();
    }
  }

  showAnnouncementModal() {
    const {title, content} = this.whitelabel.deposit.announcementModal;

    setTimeout(() => {
      const dialogRef = this.dialog.open(ConfirmationModalComponent, {
        disableClose: true,
        panelClass: 'mat-modal-normal'
      });
      dialogRef.componentInstance.title = title;
      dialogRef.componentInstance.hideCancelButton = true;
      dialogRef.componentInstance.confirmText = 'Ok';
      dialogRef.componentInstance.content = content;
    }, 0);
  }

  close() {
    this.dialogRef.close();
  }

  buildForm() {
    this.redemptionForm = this._formBuilder.group({
      login: new FormControl(this._configService.getHbAccount(), [Validators.required, Validators.minLength(2), Validators.nullValidator]),
      source: new FormControl('', [Validators.required]),
      bank: new FormControl(''),
      agency: new FormControl(''),
      account: new FormControl(''),
      favored: new FormControl(this.account, [Validators.required]),
      value: new FormControl('', [Validators.required, Validators.min(1)]),
      described_value: new FormControl('', [Validators.required]),
      total_withdrawal_checked: new FormControl(false),
      comments: new FormControl(''),
      account_group: new FormControl(''),
    });
    this.depositForm = this._formBuilder.group({
      favored: new FormControl(this.account, [Validators.required]),
      login: new FormControl(this._configService.getHbAccount(), [Validators.required]),
      bank: new FormControl('', [Validators.required]),
      agency: new FormControl('', [Validators.required]),
      account: new FormControl('', [Validators.required]),
      destination: new FormControl('', [Validators.required]),
      value: new FormControl('', [Validators.required, Validators.min(1)]),
    });
    this.addAccountValidator();

    if (this.totalWithdrawalSubscription) {
      this.totalWithdrawalSubscription.unsubscribe();
    }
    this.totalWithdrawalSubscription = this.redemptionForm.get('total_withdrawal_checked').valueChanges
      .subscribe(isTotal => {
        if (isTotal) {
          this.redemptionForm.get('value').disable();
        } else {
          this.redemptionForm.get('value').enable();
        }
      });
  }

  addAccountValidator() {
    const requiredValidator = [Validators.required];
    if (!this.showRedemptionAccount) {
      this.redemptionForm.get('bank').setValidators(requiredValidator);
      this.redemptionForm.get('agency').setValidators(requiredValidator);
      this.redemptionForm.get('account').setValidators(requiredValidator);
    } else {
      this.redemptionForm.get('account_group').setValidators(requiredValidator);
    }
    this.redemptionForm.updateValueAndValidity();
  }

  changeTab($event) {
    this.tabIndex = $event.index;
  }

  send() {
    this.loading = true;
    const paramsTemp: EmailDeposit = new EmailDeposit();
    switch (this.tabIndex) {
      case 0:
        this.redemptionForm.value.total_withdrawal_checked = this.redemptionForm.value.total_withdrawal_checked ? 'checked' : null;
        if (this.redemptionForm.status === 'VALID') {
          const bankSelect = this.redemptionForm.value.account_group;
          paramsTemp.login = this.redemptionForm.value.login;
          paramsTemp.bank = bankSelect ? bankSelect.code : this.redemptionForm.value.bank;
          paramsTemp.agency = bankSelect ? bankSelect.bankAgency : this.redemptionForm.value.agency;
          paramsTemp.account = bankSelect ? bankSelect.bankAccount : this.redemptionForm.value.account;
          paramsTemp.source = this.redemptionForm.value.source;
          paramsTemp.favored = this.redemptionForm.value.favored;
          paramsTemp.value = AppUtils.convertNumberToCurrency(this.redemptionForm.value.value);
          paramsTemp.described_value = this.redemptionForm.value.described_value;
          paramsTemp.total_withdrawal_checked = this.redemptionForm.value.total_withdrawal_checked;
          paramsTemp.comments = this.redemptionForm.value.comments;
        }
        break;
      case 1:
        if (this.depositForm.status === 'VALID') {
          paramsTemp.favored = this.depositForm.value.favored;
          paramsTemp.login = this.depositForm.value.login;
          paramsTemp.bank = this.depositForm.value.bank;
          paramsTemp.agency = this.depositForm.value.agency;
          paramsTemp.account = this.depositForm.value.account;
          paramsTemp.destination = this.depositForm.value.destination;
          paramsTemp.value = AppUtils.convertNumberToCurrency(this.depositForm.value.value);
        }
        break;
    }
    this._authServer.iEmailServices.sendEmail(this.tabIndex === 0 ? true : false, paramsTemp)
      .subscribe(res => {
        if (res && !res.error && res.code === 1) {
          this._notificationService.success('', 'Requisição de ' + (this.tabIndex === 0 ? 'RESGATE' : 'DEPÓSITO') + ' enviada com sucesso!');
          this.buildForm();
        } else {
          this._notificationService.error('', res.message || 'Ocorreu um erro no envio da Requisição de ' + (this.tabIndex === 0 ? 'RESGATE' : 'DEPÓSITO') + '!');
        }
        this.loading = false;
      }, error => {
        this._notificationService.error('', error.message || 'Ocorreu um erro no envio da Requisição de ' + (this.tabIndex === 0 ? 'RESGATE' : 'DEPÓSITO') + '!');
        this.loading = false;
      });
  }

  async getBankAccounts() {
    this.loading = true;
    const account: string = this._configService.getBrokerAccountSelected();
    const username: string = this._configService.getHbAccount();
    this.depositService.getBankAccounts(account, username).subscribe({
      next: (response) => {
        this.loading = false;
        this.listAccounts = [];
        const banks = response.listBeans || [];
        if (banks && banks.length > 0) {
          banks.forEach((acct: ISelectRedemption) => {
            this.listAccounts.push({
              bank: BANKS.find(bank => acct.bank == bank.value).label,
              bankAccount: acct.bankAccount.trim(),
              bankAgency: acct.bankAgency,
              code: acct.bank
            });
          });
          return;
        }
        this._notificationService.info('', 'Nenhuma conta bancária encontrada');
      },
      error: () => {
        this.loading = false;
        this._notificationService.error('', 'Ocorreu um erro ao listar as contas bancárias');
      }
    });
  }
}
